/**
 * Ludo Statistics Service
 *
 * Records game results via StatisticsManager, tracks per-difficulty
 * and multiplayer stats.
 *
 * Follows SnakesLaddersStatisticsService pattern.
 */

import {
  LudoStatistics,
  LudoGameResult,
  LudoGameStats,
  LudoDifficulty,
  LudoGameSettings,
  PlayerColor,
} from '../types';
import { StatisticsManager } from '../../services/StatisticsManager';

export class LudoStatisticsService {
  private statsManager: StatisticsManager;
  private readonly STATS_KEY = 'ludo_statistics';

  constructor(statsManager: StatisticsManager) {
    this.statsManager = statsManager;
  }

  private createEmptyStats(): LudoStatistics {
    return {
      singlePlayer: {
        easy: this.createEmptyGameStats(),
        medium: this.createEmptyGameStats(),
        hard: this.createEmptyGameStats(),
      },
      localMultiplayer: this.createEmptyGameStats(),
      onlineMultiplayer: this.createEmptyGameStats(),
      totalGames: 0,
      totalPlayTime: 0,
    };
  }

  private createEmptyGameStats(): LudoGameStats {
    return {
      wins: 0,
      losses: 0,
      winPercentage: 0,
      averageGameLength: 0,
      totalCaptures: 0,
    };
  }

  /** Get statistics for a user (or global if no userId). */
  getStatistics(userId?: string): LudoStatistics {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      const data = localStorage.getItem(key);
      return data ? (JSON.parse(data) as LudoStatistics) : this.createEmptyStats();
    } catch (error) {
      console.error('Failed to get ludo statistics:', error);
      return this.createEmptyStats();
    }
  }

  /** Record a completed game result. */
  recordGameResult(
    result: LudoGameResult,
    playerColor: PlayerColor,
    userId?: string
  ): boolean {
    try {
      const stats = this.getStatistics(userId);

      if (result.mode === 'singleplayer' && result.difficulty) {
        this.updateGameStats(stats.singlePlayer[result.difficulty], result, playerColor);
      } else if (result.mode === 'local-multiplayer') {
        this.updateGameStats(stats.localMultiplayer, result, playerColor);
      } else if (result.mode === 'multiplayer-realtime') {
        this.updateGameStats(stats.onlineMultiplayer, result, playerColor);
      }

      stats.totalGames++;
      stats.totalPlayTime += result.duration;

      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(stats));
      return true;
    } catch (error) {
      console.error('Failed to record ludo game result:', error);
      return false;
    }
  }

  private updateGameStats(
    stats: LudoGameStats,
    result: LudoGameResult,
    playerColor: PlayerColor
  ): void {
    const totalGames = stats.wins + stats.losses;

    if (result.winner === playerColor) {
      stats.wins++;
    } else {
      stats.losses++;
    }

    const newTotalGames = stats.wins + stats.losses;
    stats.winPercentage = newTotalGames > 0 ? (stats.wins / newTotalGames) * 100 : 0;

    // Update average game length
    stats.averageGameLength =
      newTotalGames > 0
        ? (stats.averageGameLength * totalGames + result.totalMoves) / newTotalGames
        : result.totalMoves;

    // Update captures
    stats.totalCaptures += result.capturesMade;
  }

  /** Get statistics for a specific mode and difficulty. */
  getModeStatistics(
    mode: LudoGameSettings['mode'],
    difficulty?: LudoDifficulty,
    userId?: string
  ): LudoGameStats {
    const stats = this.getStatistics(userId);

    if (mode === 'singleplayer' && difficulty) {
      return stats.singlePlayer[difficulty];
    } else if (mode === 'local-multiplayer') {
      return stats.localMultiplayer;
    } else if (mode === 'multiplayer-realtime') {
      return stats.onlineMultiplayer;
    }

    return this.createEmptyGameStats();
  }

  /** Reset all statistics. */
  resetStatistics(userId?: string): boolean {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(this.createEmptyStats()));
      return true;
    } catch (error) {
      console.error('Failed to reset ludo statistics:', error);
      return false;
    }
  }
}
